import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { Panel, TextField, RowsEditor, SaveButton, lines } from "@/components/admin/Panel";

const num = (v) => (v === "" ? 0 : Number(v));

export const PricingForm = ({ settings, reload }) => {
  const { reload: reloadSite } = useSite();
  const [p, setP] = useState(settings.pricing);
  const [pol, setPol] = useState({ ...settings.policies, extra_rules_text: settings.policies.extra_rules.join("\n") });
  const [busy, setBusy] = useState("");
  const setK = (k) => (v) => setP({ ...p, [k]: v });
  const save = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "pricing") {
        await api.put("/admin/pricing", { ...p, nightly_rate: num(p.nightly_rate), cleaning_fee: num(p.cleaning_fee), min_nights: num(p.min_nights), max_nights: num(p.max_nights),
          extra_fees: p.extra_fees.map((f) => ({ name: f.name, amount: num(f.amount) })), taxes: p.taxes.map((t) => ({ name: t.name, percent: num(t.percent) })) });
      } else {
        const { extra_rules_text, ...rest } = pol;
        await api.put("/admin/policies", { ...rest, extra_rules: lines(extra_rules_text) });
      }
      toast.success("Saved");
      reload(); reloadSite();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  return (
    <>
      <Panel title="Rates & fees" desc={settings.pricing.is_sample ? "These are sample values shown as demonstration rates. Saving replaces them with your real pricing." : "Your live pricing."} testid="pricing-panel">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <TextField label="Nightly rate (USD)" type="number" value={p.nightly_rate} onChange={setK("nightly_rate")} testid="pricing-nightly" />
          <TextField label="Cleaning fee (USD)" type="number" value={p.cleaning_fee} onChange={setK("cleaning_fee")} testid="pricing-cleaning" />
          <TextField label="Minimum nights" type="number" value={p.min_nights} onChange={setK("min_nights")} testid="pricing-min-nights" />
          <TextField label="Maximum nights" type="number" value={p.max_nights} onChange={setK("max_nights")} testid="pricing-max-nights" />
          <TextField label="Check-in time" value={p.checkin_time} onChange={setK("checkin_time")} testid="pricing-checkin" />
          <TextField label="Check-out time" value={p.checkout_time} onChange={setK("checkout_time")} testid="pricing-checkout" />
        </div>
        <div className="grid lg:grid-cols-2 gap-8 mt-8">
          <div><p className="font-semibold text-sm mb-2">Additional fees (per stay)</p><RowsEditor rows={p.extra_fees} setRows={setK("extra_fees")} fields={[["name", "Fee name"], ["amount", "USD", "number"]]} blank={{ name: "", amount: 0 }} testid="pricing-fees" /></div>
          <div><p className="font-semibold text-sm mb-2">Taxes (percent of stay total)</p><RowsEditor rows={p.taxes} setRows={setK("taxes")} fields={[["name", "Tax name"], ["percent", "%", "number"]]} blank={{ name: "", percent: 0 }} testid="pricing-taxes" /></div>
        </div>
        <div className="mt-6"><SaveButton onClick={() => save("pricing")} busy={busy === "pricing"} testid="save-pricing-button" label="Save pricing" /></div>
      </Panel>
      <Panel title="Booking & cancellation policies" desc="Shown on the policy pages and booking page." testid="policies-panel">
        <div className="space-y-4">
          <TextField area rows={4} label="Cancellation policy" value={pol.cancellation} onChange={(v) => setPol({ ...pol, cancellation: v })} testid="policy-cancellation" />
          <TextField area rows={2} label="Payment terms" value={pol.payment_terms} onChange={(v) => setPol({ ...pol, payment_terms: v })} testid="policy-payment-terms" />
          <TextField area rows={2} label="Additional house rules (one per line)" value={pol.extra_rules_text} onChange={(v) => setPol({ ...pol, extra_rules_text: v })} testid="policy-extra-rules" />
          <TextField area rows={5} label="Privacy policy" value={pol.privacy} onChange={(v) => setPol({ ...pol, privacy: v })} testid="policy-privacy" />
          <TextField area rows={5} label="Terms of service" value={pol.terms} onChange={(v) => setPol({ ...pol, terms: v })} testid="policy-terms" />
        </div>
        <div className="mt-6"><SaveButton onClick={() => save("policies")} busy={busy === "policies"} testid="save-policies-button" label="Save policies" /></div>
      </Panel>
    </>
  );
};
